import React from 'react';

// Baseline growth matrix: prior-year actuals → YTD actuals → forecast horizon,
// with year-over-year growth deltas so the embedded growth rate of the
// baseline is visible before any market input. Expense-side metrics invert
// the variance color (growth in costs reads red).

const METRICS = [
  { key: 'loans', label: 'Loans (EOP)', digits: 0 },
  { key: 'deposits', label: 'Deposits (EOP)', digits: 0 },
  { key: 'nii', label: 'NII', digits: 1 },
  { key: 'fees', label: 'Noninterest income', digits: 1 },
  { key: 'expense', label: 'Noninterest expense', digits: 1, lowerBetter: true },
  { key: 'provision', label: 'Provision', digits: 1, lowerBetter: true },
  { key: 'netIncome', label: 'Net income', digits: 1 }
];

const fmt = (value, digits) =>
  value === null || value === undefined || Number.isNaN(value)
    ? '—'
    : `$${Number(value).toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;

const ForecastGrowthMatrix = ({ columns, title, note }) => {
  if (!columns || columns.length === 0) return null;
  // A step-change between the last actual column and the first forecast column
  // usually means the baseline is calibrated at a different grain (market vs
  // entity) — flag it rather than let the growth read as real.
  const lastActualIndex = columns.reduce((best, column, index) => (column.actual ? index : best), -1);
  const calibrationJump = lastActualIndex >= 0 && lastActualIndex + 1 < columns.length
    && ['deposits', 'nii', 'expense'].some((key) => {
      const actualValue = columns[lastActualIndex].values[key];
      const forecastValue = columns[lastActualIndex + 1].values[key];
      return actualValue && forecastValue && Math.abs(forecastValue / actualValue) > 2.5;
    });
  return (
    <div className="forecast-subpanel growth-matrix">
      <div className="forecast-panel-head">
        <span className="forecast-section-code">{title || 'Baseline growth path'}</span>
        <h3>Prior-year actuals → YTD run-rate → forecast horizon, with YoY growth</h3>
      </div>
      <div className="forecast-table-shell ace-scroll-shell forecast-compact-table">
        <table className="forecast-table forecast-time-matrix">
          <thead>
            <tr>
              <th>Metric · $MM</th>
              {columns.map((column) => (
                <th key={column.label} className={column.actual ? 'time-col yt' : 'time-col y'}>
                  {column.label}
                  {column.sub && <small className="growth-col-sub">{column.sub}</small>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {METRICS.map((metric) => (
              <tr key={metric.key}>
                <td><strong>{metric.label}</strong></td>
                {columns.map((column, columnIndex) => {
                  const value = column.values[metric.key];
                  const previous = columnIndex > 0 ? columns[columnIndex - 1].values[metric.key] : null;
                  const delta = previous ? ((value - previous) / Math.abs(previous)) * 100 : null;
                  const favorable = delta !== null && (metric.lowerBetter ? delta <= 0 : delta >= 0);
                  return (
                    <td key={column.label} className="time-cell growth-cell">
                      <span className="forecast-mono">{fmt(value, metric.digits)}</span>
                      {delta !== null && Number.isFinite(delta) && (
                        <small className={favorable ? 'variance-pos' : 'variance-neg'}>
                          {delta >= 0 ? '+' : ''}{delta.toFixed(1)}%
                        </small>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {calibrationJump && (
        <p className="forecast-submit-note" style={{ borderLeft: '2px solid #d9534f', paddingLeft: 10 }}>
          Calibration note: the step-change between the actual columns and the first forecast year
          reflects baseline grain (market-level baseline vs entity-level actuals — the known Phase-3
          calibration item), not real growth. Read the year-over-year deltas within the forecast
          horizon; treat the actuals-to-forecast jump as a scale artifact.
        </p>
      )}
      <p className="forecast-submit-note">
        {note || 'Each delta is growth versus the column to its left (the YTD column annualizes flows; balances are end-of-period). Expense and provision growth invert the color — rising costs read red.'}
      </p>
    </div>
  );
};

export default ForecastGrowthMatrix;
